import React, { useState, useEffect } from 'react';
import { QRScanner } from '../components/QRScanner';
import axios from '../utils/axios';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Award, ArrowLeft, Calendar, Mail, Lock, QrCode as QrCodeIcon, Eye, EyeOff } from 'lucide-react';

interface EventData {
  type?: string;
  event_id: number;
  title: string;
  timestamp?: number;
}

interface CheckInState {
  eventData: EventData | null;
  awaitingLogin: boolean;
  checking: boolean;
  success: {
    eventTitle: string;
    points: number;
    memberEmail: string;
    memberName: string;
  } | null;
}

const EventCheckInPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<CheckInState>({
    eventData: null,
    awaitingLogin: false,
    checking: false,
    success: null
  });
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Handle event from URL (native camera scan)
  useEffect(() => {
    const eventId = searchParams.get('event');
    const eventTitle = searchParams.get('title');

    if (eventId) {
      setState({
        eventData: { event_id: parseInt(eventId), title: eventTitle || 'Event', timestamp: Date.now() },
        awaitingLogin: true,
        checking: false,
        success: null
      });
      toast.info('Event detected! Enter your credentials to mark attendance.');
    }
  }, [searchParams]);

  const handleScan = async (qrData: string) => {
    try {
      let eventData: EventData;

      if (qrData.startsWith('http://') || qrData.startsWith('https://')) {
        const url = new URL(qrData);
        const eventId = url.searchParams.get('event');
        const eventTitle = url.searchParams.get('title');
        if (!eventId) throw new Error('Invalid QR code. Missing event information.');
        eventData = { event_id: parseInt(eventId), title: eventTitle || 'Event', timestamp: Date.now() };
      } else {
        try {
          eventData = JSON.parse(qrData);
        } catch {
          throw new Error('Invalid QR code format');
        }
        if (eventData.type !== 'event') throw new Error('This is not an event QR code.');
        if (!eventData.event_id) throw new Error('Invalid event QR code. Missing event ID.');
      }

      setState({ eventData, awaitingLogin: true, checking: false, success: null });
      toast.info('Event scanned! Enter your member credentials to check in.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to scan QR code. Please try again.');
    }
  };

  const handleCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.eventData) return;

    setState(prev => ({ ...prev, checking: true }));

    try {
      const response = await axios.post('/attendance/check-in-with-credentials', {
        identifier: identifier.trim(),
        password,
        eventId: state.eventData.event_id
      });

      const { event_title, points_earned, member_name, member_email } = response.data.data;

      setState({
        eventData: null,
        awaitingLogin: false,
        checking: false,
        success: {
          eventTitle: event_title || state.eventData.title,
          points: points_earned || 0,
          memberEmail: member_email || identifier,
          memberName: member_name || ''
        }
      });

      toast.success(`Check-in successful! You earned ${points_earned} points!`);
      setTimeout(() => navigate('/member/login'), 5000);
    } catch (error: any) {
      setState(prev => ({ ...prev, checking: false }));
      const msg = error.response?.data?.error?.message || error.response?.data?.error || error.message;
      if (msg?.includes('already checked in')) {
        toast.error('You have already checked in to this event!');
      } else if (msg?.includes('No registered member') || msg?.includes('Invalid credentials') || msg?.includes('not found')) {
        toast.error('Invalid email or password. Please check your credentials.');
      } else if (msg?.includes('pending approval')) {
        toast.error('Your membership is pending approval. Please contact administration.');
      } else if (msg?.includes('inactive')) {
        toast.error('Your account is inactive. Please contact administration.');
      } else {
        toast.error(msg || 'Check-in failed. Please try again.');
      }
    }
  };

  const resetScan = () => {
    setState({ eventData: null, awaitingLogin: false, checking: false, success: null });
    setIdentifier('');
    setPassword('');
    navigate('/member/check-in', { replace: true });
  };

  // Success Screen
  if (state.success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center py-8 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-[#0D5128] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={48} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Check-in Successful!</h1>
          <p className="text-gray-500 mb-4 text-sm">Welcome, {state.success.memberName}</p>

          <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
            <h2 className="text-base font-semibold text-gray-900 mb-3">{state.success.eventTitle}</h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Award size={20} className="text-[#C49A3C]" />
              <span className="font-bold text-2xl text-[#C49A3C]">+{state.success.points} pts</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
              <Mail size={14} />
              <span>{state.success.memberEmail}</span>
            </div>
          </div>

          <p className="text-sm text-gray-400 mb-6">Redirecting to login in 5 seconds...</p>
          <button onClick={() => navigate('/member/login')} className="w-full py-3 bg-[#0D5128] hover:bg-green-800 text-white rounded-lg font-medium transition-colors">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Login Screen (after QR scan)
  if (state.awaitingLogin && state.eventData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8 px-4">
        <div className="max-w-md mx-auto">
          <button onClick={resetScan} className="flex items-center gap-2 text-[#0D5128] hover:text-green-900 mb-6">
            <ArrowLeft size={20} />
            Scan Different QR Code
          </button>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-[#0D5128] px-6 py-5 text-white text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <QrCodeIcon size={24} className="text-white" />
              </div>
              <h1 className="text-xl font-bold">Event Check-In</h1>
              <p className="text-green-200 text-sm mt-1">{state.eventData.title}</p>
            </div>

            {/* Form */}
            <div className="px-6 py-6">
              <p className="text-sm text-gray-500 text-center mb-5">
                Enter your member credentials to mark attendance
              </p>

              <form onSubmit={handleCheckIn} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email or Member ID
                    <span className="ml-1 text-xs text-gray-400 font-normal">(e.g. 15501 or your@email.com)</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={identifier}
                      onChange={e => setIdentifier(e.target.value)}
                      required
                      placeholder="15501 or your@email.com"
                      className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D5128] focus:border-transparent text-sm"
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D5128] focus:border-transparent text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={state.checking}
                  className="w-full py-3 bg-[#0D5128] hover:bg-green-800 disabled:opacity-60 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {state.checking ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      Marking attendance...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Mark Attendance
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <ul className="text-xs text-amber-800 space-y-1">
                  <li>• Enter your Member ID (e.g. 15501) or registered email</li>
                  <li>• Only approved members can check in</li>
                  <li>• You can only check in once per event</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // QR Scanner Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/member/login')} className="flex items-center gap-2 text-[#0D5128] hover:text-green-900 mb-6">
          <ArrowLeft size={20} />
          Back to Login
        </button>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-[#0D5128] px-6 py-5 text-white text-center">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Calendar size={24} className="text-white" />
            </div>
            <h1 className="text-xl font-bold">Event Check-In</h1>
            <p className="text-green-200 text-sm mt-1">Scan the event QR code</p>
          </div>

          <div className="p-6">
            <QRScanner
              onScan={handleScan}
              onError={(error) => console.error('Scanner error:', error)}
              className="mb-6"
            />

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mt-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm">How to check in:</h3>
              <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                <li>Scan the event QR code displayed at the venue</li>
                <li>Enter your member email and password</li>
                <li>Points are credited automatically</li>
              </ol>
            </div>

            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
              <QrCodeIcon size={16} className="text-[#0D5128] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-800">You can also open your phone camera, scan the QR code, and tap the link — then enter your credentials.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCheckInPage;
